/**
 * Marketing Automation Module Page
 * Multi-channel campaigns (Email, SMS, WhatsApp, Voice AI, Social Media),
 * automation workflows, contacts, and analytics
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useTabbedChrome } from '@/components/weavein';
import { config } from '@/src/config';
import { AgentAssistantPanel } from '../../shared/components/AgentAssistant';
import CampaignChannelChip, { CampaignChannelIcon } from './CampaignChannelChip';
import {
  Mail,
  Users,
  Zap,
  BarChart3,
  Send,
  Plus,
  Calendar,
  Target,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Edit,
  Play,
  Pause,
  Trash2,
  Upload,
  Check,
  RefreshCw,
  Loader2,
  AlertCircle,
  X,
  ArrowLeft,
  Save,
  FileText,
  MessageSquare,
  Phone,
  Globe,
  Copy,
  Variable,
  Plug,
} from 'lucide-react';
// Marketplace integrations live as a sub-tab inside Marketing per IA decision
// (instead of a standalone sidebar item). Lazy-imported via the regular page
// component since it's already part of the platform bundle.
import { MarketplaceIntegrationsPage } from './MarketplaceIntegrationsPage';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { marketingApi, Campaign as ApiCampaign, contactListsApi, ContactListFull } from '../../shared/services/platformApi';
import { RichTextEditor } from '../../modules/web-presence/components/RichTextEditor/RichTextEditor';

// ============================================================================
// TYPES
// ============================================================================

type MarketingTab = 'campaigns' | 'automation' | 'lists' | 'analytics' | 'integrations';

interface Campaign {
  id: string;
  name: string;
  type: 'email' | 'sms' | 'whatsapp' | 'voice' | 'social' | 'multi-channel';
  channels?: ('email' | 'sms' | 'whatsapp' | 'voice' | 'social')[];  // For multi-channel campaigns
  status: 'draft' | 'scheduled' | 'running' | 'completed' | 'paused';
  sentCount: number;
  openRate: number;
  clickRate: number;
  scheduledDate?: string;
  subject?: string;
}

interface Automation {
  id: string;
  name: string;
  trigger: string;
  status: 'active' | 'paused' | 'draft';
  enrolled: number;
  completed: number;
  steps: number;
  // Engine fields kept for the edit flow.
  triggerType?: string;
  triggerConfig?: Record<string, unknown>;
  actionType?: string;
  actionConfig?: Record<string, unknown>;
}

interface ContactList {
  id: string;
  name: string;
  count: number;
  type: 'static' | 'dynamic';
}

// ============================================================================
// EXTENDED CAMPAIGN TYPE WITH FULL DETAILS
// ============================================================================

interface CampaignDetails extends Campaign {
  // Audience/Contact Group info
  audienceId?: string;
  audienceName?: string;
  audienceCount?: number;
  // Content info
  templateId?: string;
  templateName?: string;
  description?: string;
  // Organization/ICP info
  organizationId?: number;
  organizationName?: string;
}

// ============================================================================
// MODAL COMPONENTS
// ============================================================================

interface CreateCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (campaign: Omit<Campaign, 'id' | 'sentCount' | 'openRate' | 'clickRate'> & { audienceId?: string; audienceName?: string; audienceCount?: number }) => void;
  contactLists: ContactListFull[];
  loadingContactLists: boolean;
}

type ChannelType = 'email' | 'sms' | 'whatsapp' | 'voice' | 'social';

const CHANNEL_OPTIONS: { value: ChannelType; label: string; icon: string }[] = [
  { value: 'email', label: 'Email', icon: '📧' },
  { value: 'sms', label: 'SMS', icon: '📱' },
  { value: 'whatsapp', label: 'WhatsApp', icon: '💬' },
  { value: 'voice', label: 'Voice AI', icon: '📞' },
  { value: 'social', label: 'Social Media', icon: '🌐' },
];

const getCampaignTypeInfo = (type: Campaign['type'], channels?: ChannelType[]): { icon: string; label: string; color: string } => {
  switch (type) {
    case 'email':
      return { icon: '📧', label: 'Email', color: 'bg-blue-100 text-blue-800' };
    case 'sms':
      return { icon: '📱', label: 'SMS', color: 'bg-green-100 text-green-800' };
    case 'whatsapp':
      return { icon: '💬', label: 'WhatsApp', color: 'bg-emerald-100 text-emerald-800' };
    case 'voice':
      return { icon: '📞', label: 'Voice AI', color: 'bg-purple-100 text-purple-800' };
    case 'social':
      return { icon: '🌐', label: 'Social', color: 'bg-pink-100 text-pink-800' };
    case 'multi-channel':
      const channelIcons = channels?.map(c => CHANNEL_OPTIONS.find(o => o.value === c)?.icon).join(' ') || '🔀';
      return { icon: channelIcons, label: 'Multi-Channel', color: 'bg-indigo-100 text-indigo-800' };
    default:
      return { icon: '📧', label: 'Email', color: 'bg-gray-100 text-gray-800' };
  }
};

function CreateCampaignModal({ isOpen, onClose, onSubmit, contactLists, loadingContactLists }: CreateCampaignModalProps) {
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [type, setType] = useState<Campaign['type']>('email');
  const [selectedChannels, setSelectedChannels] = useState<ChannelType[]>([]);
  const [scheduledDate, setScheduledDate] = useState('');
  const [selectedListId, setSelectedListId] = useState<string>('');

  const handleChannelToggle = (channel: ChannelType) => {
    setSelectedChannels(prev =>
      prev.includes(channel)
        ? prev.filter(c => c !== channel)
        : [...prev, channel]
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const selectedList = contactLists.find(l => l.id === selectedListId);
    const campaignData: Omit<Campaign, 'id' | 'sentCount' | 'openRate' | 'clickRate'> & { audienceId?: string; audienceName?: string; audienceCount?: number } = {
      name,
      subject,
      type,
      status: scheduledDate ? 'scheduled' : 'draft',
      scheduledDate: scheduledDate || undefined,
      audienceId: selectedListId || undefined,
      audienceName: selectedList?.name,
      audienceCount: selectedList?.member_count,
    };

    // Add channels array for multi-channel campaigns
    if (type === 'multi-channel' && selectedChannels.length > 0) {
      campaignData.channels = selectedChannels;
    }

    onSubmit(campaignData);
    setName('');
    setSubject('');
    setType('email');
    setSelectedChannels([]);
    setScheduledDate('');
    setSelectedListId('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create New Campaign</DialogTitle>
          <DialogDescription>
            Set up a new multi-channel marketing campaign.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Campaign Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter campaign name"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subject / Message *
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter subject or message content"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Campaign Type
            </label>
            <select
              value={type}
              onChange={(e) => {
                const newType = e.target.value as Campaign['type'];
                setType(newType);
                if (newType !== 'multi-channel') {
                  setSelectedChannels([]);
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="email">📧 Email Campaign</option>
              <option value="sms">📱 SMS Campaign</option>
              <option value="whatsapp">💬 WhatsApp Campaign</option>
              <option value="voice">📞 Voice AI Campaign</option>
              <option value="social">🌐 Social Media Campaign</option>
              <option value="multi-channel">🔀 Multi-Channel Campaign</option>
            </select>
          </div>

          {/* Multi-channel selection */}
          {type === 'multi-channel' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Channels *
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {CHANNEL_OPTIONS.map((channel) => (
                  <label
                    key={channel.value}
                    className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedChannels.includes(channel.value)
                        ? 'bg-blue-50 border-blue-500 text-blue-700'
                        : 'bg-white border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedChannels.includes(channel.value)}
                      onChange={() => handleChannelToggle(channel.value)}
                      className="sr-only"
                    />
                    <span>{channel.icon}</span>
                    <span className="text-sm font-medium">{channel.label}</span>
                    {selectedChannels.includes(channel.value) && (
                      <Check className="w-4 h-4 ml-auto" />
                    )}
                  </label>
                ))}
              </div>
              {selectedChannels.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  Select at least one channel for multi-channel campaigns
                </p>
              )}
            </div>
          )}

          {/* Audience/Contact List Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Audience (Contact List)
            </label>
            {loadingContactLists ? (
              <div className="flex items-center gap-2 py-2 text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">Loading contact lists...</span>
              </div>
            ) : (
              <select
                value={selectedListId}
                onChange={(e) => setSelectedListId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">-- Select a contact list (optional) --</option>
                {contactLists.map((list) => (
                  <option key={list.id} value={list.id}>
                    {list.name} ({list.member_count} contacts) {list.list_type === 'dynamic' ? '[Dynamic]' : ''}
                  </option>
                ))}
              </select>
            )}
            {contactLists.length === 0 && !loadingContactLists && (
              <p className="text-xs text-amber-600 mt-1">
                No contact lists found. Create lists in the Lists & Segments tab.
              </p>
            )}
            {selectedListId && (
              <p className="text-xs text-green-600 mt-1">
                ✓ Will target {contactLists.find(l => l.id === selectedListId)?.member_count || 0} contacts
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Schedule (Optional)
            </label>
            <input
              type="datetime-local"
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Leave empty to save as draft
            </p>
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={type === 'multi-channel' && selectedChannels.length === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Create Campaign
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// FULL PAGE CAMPAIGN EDITOR
// ============================================================================

// Template variables available for personalization
const CAMPAIGN_VARIABLES = [
  { key: '{{first_name}}', label: 'First Name', example: 'Maria' },
  { key: '{{last_name}}', label: 'Last Name', example: 'Garcia' },
  { key: '{{email}}', label: 'Email Address', example: 'maria@example.com' },
  { key: '{{company}}', label: 'Company Name', example: 'Acme Corp' },
  { key: '{{phone}}', label: 'Phone Number', example: '(555) 123-4567' },
  { key: '{{unsubscribe_link}}', label: 'Unsubscribe Link', example: '[Unsubscribe]' },
];

// Pre-built templates for quick start
const CAMPAIGN_TEMPLATES = [
  {
    id: 'blank',
    name: 'Blank Template',
    description: 'Start from scratch',
    channels: ['email', 'sms', 'whatsapp', 'voice', 'social'] as ChannelType[],
    content: { email: { subject: '', body: '' }, sms: '', whatsapp: '', voice: '', social: '' },
  },
  {
    id: 'welcome',
    name: 'Welcome Email',
    description: 'Onboarding new contacts',
    channels: ['email'] as ChannelType[],
    content: {
      email: {
        subject: 'Welcome to {{company}}!',
        body: `Hi {{first_name}},\n\nWelcome to our community! We're excited to have you on board.\n\nAs a valued member, you'll receive:\n• Exclusive updates and insights\n• Early access to new features\n• Special offers and promotions\n\nIf you have any questions, don't hesitate to reach out.\n\nBest regards,\nThe Team`,
      },
      sms: '',
      whatsapp: '',
      voice: '',
      social: '',
    },
  },
  {
    id: 'newsletter',
    name: 'Newsletter',
    description: 'Regular newsletter template',
    channels: ['email'] as ChannelType[],
    content: {
      email: {
        subject: 'Your Monthly Update from {{company}}',
        body: `Hi {{first_name}},\n\nHere's what's new this month:\n\n[Add your content here]\n\n---\n\nStay connected with us!\n\nBest,\nThe {{company}} Team\n\n{{unsubscribe_link}}`,
      },
      sms: '',
      whatsapp: '',
      voice: '',
      social: '',
    },
  },
  {
    id: 'promo',
    name: 'Promotional Offer',
    description: 'Sales and promotions',
    channels: ['email', 'sms'] as ChannelType[],
    content: {
      email: {
        subject: 'Special Offer Just for You, {{first_name}}!',
        body: `Hi {{first_name}},\n\nWe have an exclusive offer waiting for you!\n\n[Describe your offer here]\n\nDon't miss out - this offer expires soon!\n\nBest regards,\nThe Team\n\n{{unsubscribe_link}}`,
      },
      sms: 'Hi {{first_name}}! Special offer from {{company}}: [Your offer]. Reply STOP to opt out.',
      whatsapp: '',
      voice: '',
      social: '',
    },
  },
  {
    id: 'reminder',
    name: 'Appointment Reminder',
    description: 'Remind contacts of upcoming events',
    channels: ['email', 'sms', 'whatsapp'] as ChannelType[],
    content: {
      email: {
        subject: 'Reminder: Your Upcoming Appointment',
        body: `Hi {{first_name}},\n\nThis is a friendly reminder about your upcoming appointment.\n\n[Add appointment details here]\n\nIf you need to reschedule, please contact us.\n\nBest regards,\nThe Team`,
      },
      sms: 'Hi {{first_name}}, reminder: Your appointment is coming up. Reply to confirm or call to reschedule.',
      whatsapp: 'Hi {{first_name}}! Just a reminder about your upcoming appointment. Please confirm your attendance.',
      voice: '',
      social: '',
    },
  },
  {
    id: 'outreach',
    name: 'Multi-Channel Outreach',
    description: 'Coordinated multi-channel campaign',
    channels: ['email', 'sms', 'whatsapp', 'voice'] as ChannelType[],
    content: {
      email: {
        subject: 'Important Information from {{company}}',
        body: `Hi {{first_name}},\n\nWe wanted to reach out with some important information.\n\n[Your content here]\n\nPlease feel free to contact us if you have any questions.\n\nBest regards,\nThe Team`,
      },
      sms: 'Hi {{first_name}}, {{company}} here with important info. Check your email for details!',
      whatsapp: 'Hello {{first_name}}! We have important information for you. Please check your email or call us.',
      voice: 'Hello {{first_name}}, this is {{company}} calling with important information. Please check your email for details or press 1 to speak with a representative.',
      social: '',
    },
  },
];

interface CampaignEditorProps {
  campaign?: CampaignDetails | null;
  contactLists: ContactListFull[];
  loadingContactLists: boolean;
  onSave: (campaign: Partial<CampaignDetails> & { channelContent?: Record<string, unknown> }) => Promise<void>;
  onBack: () => void;
  mode: 'create' | 'edit';
}

function CampaignEditor({ campaign, contactLists, loadingContactLists, onSave, onBack, mode }: CampaignEditorProps) {
  // Basic info
  const [name, setName] = useState(campaign?.name || '');
  const [type, setType] = useState<Campaign['type']>(campaign?.type || 'email');
  const [selectedChannels, setSelectedChannels] = useState<ChannelType[]>(
    campaign?.channels || (campaign?.type && campaign.type !== 'multi-channel' ? [campaign.type as ChannelType] : ['email'])
  );
  const [selectedListId, setSelectedListId] = useState<string>(campaign?.audienceId || '');
  const [scheduledDate, setScheduledDate] = useState(campaign?.scheduledDate || '');

  // Channel content
  const [emailSubject, setEmailSubject] = useState(campaign?.subject || '');
  const [emailBody, setEmailBody] = useState(campaign?.description || '');
  const [smsContent, setSmsContent] = useState('');
  const [whatsappContent, setWhatsappContent] = useState('');
  const [voiceScript, setVoiceScript] = useState('');
  const [socialContent, setSocialContent] = useState('');

  // Drip sequence — ordered channel steps with delays (Option B). This is the
  // persisted model: each step is one message on one channel, sent to the
  // audience contact list after a cumulative delay.
  type StepDraft = { channel: ChannelType; subject?: string; content: string; delay_hours: number };
  // Seed one email step so the create flow opens straight into the composer.
  const [steps, setSteps] = useState<StepDraft[]>(
    mode === 'create' ? [{ channel: 'email', subject: '', content: '', delay_hours: 0 }] : [],
  );
  const [selectedStepIndex, setSelectedStepIndex] = useState(0);
  useEffect(() => {
    if (mode === 'edit' && campaign?.id) {
      marketingApi.getCampaignSteps(campaign.id).then((r: any) => {
        const rows = r?.data ?? r ?? [];
        if (Array.isArray(rows) && rows.length) {
          setSteps(rows.map((s: any) => ({ channel: s.channel, subject: s.subject || '', content: s.content || '', delay_hours: s.delay_hours || 0 })));
          setSelectedStepIndex(0);
        }
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, campaign?.id]);
  const addStep = () => setSteps((p) => {
    const next = [...p, { channel: 'email' as ChannelType, subject: '', content: '', delay_hours: p.length === 0 ? 0 : 24 }];
    setSelectedStepIndex(next.length - 1);
    return next;
  });
  const updateStep = (i: number, patch: Partial<StepDraft>) => setSteps((p) => p.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const removeStep = (i: number) => setSteps((p) => {
    const n = p.filter((_, j) => j !== i);
    setSelectedStepIndex((cur) => Math.max(0, Math.min(cur, n.length - 1)));
    return n;
  });
  const moveStep = (i: number, dir: -1 | 1) => setSteps((p) => { const j = i + dir; if (j < 0 || j >= p.length) return p; const n = [...p]; [n[i], n[j]] = [n[j], n[i]]; setSelectedStepIndex(j); return n; });

  // UI state
  const [activeChannel, setActiveChannel] = useState<ChannelType>('email');
  const [showTemplates, setShowTemplates] = useState(mode === 'create');
  const [showVariables, setShowVariables] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Character counts for SMS
  const smsCharCount = smsContent.length;
  const smsSegments = Math.ceil(smsCharCount / 160) || 1;

  // Ensure active channel is one of the selected channels
  useEffect(() => {
    if (!selectedChannels.includes(activeChannel) && selectedChannels.length > 0) {
      setActiveChannel(selectedChannels[0]);
    }
  }, [selectedChannels, activeChannel]);

  const handleChannelToggle = (channel: ChannelType) => {
    setSelectedChannels(prev => {
      const newChannels = prev.includes(channel)
        ? prev.filter(c => c !== channel)
        : [...prev, channel];
      // Update type based on channels
      if (newChannels.length === 1) {
        setType(newChannels[0] as Campaign['type']);
      } else if (newChannels.length > 1) {
        setType('multi-channel');
      }
      return newChannels;
    });
  };

  const applyTemplate = (template: typeof CAMPAIGN_TEMPLATES[0]) => {
    if (template.content.email) {
      setEmailSubject(template.content.email.subject);
      setEmailBody(template.content.email.body);
    }
    if (template.content.sms) setSmsContent(template.content.sms);
    if (template.content.whatsapp) setWhatsappContent(template.content.whatsapp);
    if (template.content.voice) setVoiceScript(template.content.voice);
    if (template.content.social) setSocialContent(template.content.social);
    setSelectedChannels(template.channels);
    if (template.channels.length === 1) {
      setType(template.channels[0] as Campaign['type']);
      setActiveChannel(template.channels[0]);
    } else if (template.channels.length > 1) {
      setType('multi-channel');
      setActiveChannel(template.channels[0]);
    }
    setShowTemplates(false);
  };

  const insertVariable = (variable: string) => {
    // Insert variable at cursor position in active content area
    const textarea = document.activeElement as HTMLTextAreaElement | HTMLInputElement;
    if (textarea && (textarea.tagName === 'TEXTAREA' || textarea.tagName === 'INPUT')) {
      const start = textarea.selectionStart || 0;
      const end = textarea.selectionEnd || 0;
      const value = textarea.value;
      const newValue = value.substring(0, start) + variable + value.substring(end);

      // Update the appropriate state based on which field is active
      if (activeChannel === 'email') {
        if (textarea.id === 'email-subject') {
          setEmailSubject(newValue);
        } else if (textarea.id === 'email-body') {
          setEmailBody(newValue);
        }
      } else if (activeChannel === 'sms') {
        setSmsContent(newValue);
      } else if (activeChannel === 'whatsapp') {
        setWhatsappContent(newValue);
      } else if (activeChannel === 'voice') {
        setVoiceScript(newValue);
      } else if (activeChannel === 'social') {
        setSocialContent(newValue);
      }
    }
  };

  const handleSave = async (saveAsDraft = false) => {
    if (!name.trim()) {
      setSaveError('Please enter a campaign name');
      return;
    }

    if (selectedChannels.length === 0) {
      setSaveError('Please select at least one channel');
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      const selectedList = contactLists.find(l => l.id === selectedListId);
      // Persisted drip steps. If the user didn't build a sequence, seed one step
      // per selected channel from that channel's content (so their input isn't lost).
      const channelBody: Record<string, string> = {
        email: emailBody, sms: smsContent, whatsapp: whatsappContent, voice: voiceScript, social: socialContent,
      };
      const effectiveSteps = steps.length
        ? steps
        : selectedChannels.map((ch, i) => ({
            channel: ch,
            subject: ch === 'email' ? emailSubject : undefined,
            content: channelBody[ch] || '',
            delay_hours: i === 0 ? 0 : 24,
          }));
      const campaignData: Partial<CampaignDetails> & { channelContent?: Record<string, unknown>; steps?: StepDraft[] } = {
        id: campaign?.id,
        name,
        type,
        channels: selectedChannels,
        status: saveAsDraft ? 'draft' : (scheduledDate ? 'scheduled' : 'draft'),
        scheduledDate: scheduledDate || undefined,
        subject: emailSubject,
        description: emailBody,
        audienceId: selectedListId || undefined,
        audienceName: selectedList?.name,
        audienceCount: selectedList?.member_count,
        steps: effectiveSteps,
        channelContent: {
          email: { subject: emailSubject, body: emailBody },
          sms: smsContent,
          whatsapp: whatsappContent,
          voice: voiceScript,
          social: socialContent,
        },
      };

      await onSave(campaignData);
      onBack();
    } catch (err) {
      console.error('Failed to save campaign:', err);
      setSaveError('Failed to save campaign. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const getChannelIcon = (channel: ChannelType) => {
    switch (channel) {
      case 'email': return <Mail className="h-4 w-4" />;
      case 'sms': return <MessageSquare className="h-4 w-4" />;
      case 'whatsapp': return <MessageSquare className="h-4 w-4" />;
      case 'voice': return <Phone className="h-4 w-4" />;
      case 'social': return <Globe className="h-4 w-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header — name, audience, save/launch */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-3">
        <div className="flex items-center gap-3 max-w-7xl mx-auto">
          <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="Back">
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Untitled campaign"
            className="flex-1 min-w-0 text-lg font-semibold text-gray-900 bg-transparent border-0 border-b border-transparent focus:border-gray-300 focus:outline-none px-1 py-1"
          />
          <div className="hidden md:flex items-center gap-2">
            <span className="text-xs text-gray-500">Audience</span>
            <select
              value={selectedListId}
              onChange={(e) => setSelectedListId(e.target.value)}
              className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm max-w-[220px]"
            >
              <option value="">Select a list…</option>
              {contactLists.map((l) => (
                <option key={l.id} value={l.id}>{l.name} ({l.member_count})</option>
              ))}
            </select>
          </div>
          <button
            onClick={() => handleSave(true)}
            disabled={isSaving}
            className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50 text-sm"
          >
            <Save className="h-4 w-4" /> Save draft
          </button>
          <button
            onClick={() => handleSave(false)}
            disabled={isSaving || steps.length === 0}
            className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50 text-sm"
          >
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {isSaving ? 'Saving…' : (scheduledDate ? 'Schedule' : 'Save & Launch')}
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {saveError && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <span className="text-red-700">{saveError}</span>
            <button onClick={() => setSaveError(null)} className="ml-auto"><X className="h-4 w-4 text-red-600" /></button>
          </div>
        )}

        {/* Audience on small screens (header hides it) */}
        <div className="md:hidden mb-4 bg-white rounded-lg border border-gray-200 p-4">
          <label className="text-sm text-gray-700">Audience
            <select value={selectedListId} onChange={(e) => setSelectedListId(e.target.value)} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value="">Select a contact list…</option>
              {contactLists.map((l) => <option key={l.id} value={l.id}>{l.name} ({l.member_count})</option>)}
            </select>
          </label>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">
          {/* Sequence rail */}
          <aside className="space-y-3">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-sm font-semibold text-gray-900">Sequence</h2>
                <button onClick={addStep} className="flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800"><Plus className="h-4 w-4" /> Add</button>
              </div>
              <p className="text-xs text-gray-500 mb-3">Steps send in order to your audience.</p>
              <div>
                {steps.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedStepIndex(i)}
                    className={`w-full text-left flex items-start gap-2 p-2 rounded-lg border transition-colors ${i === selectedStepIndex ? 'border-blue-400 bg-blue-50' : 'border-transparent hover:bg-gray-50'}`}
                  >
                    <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${i === selectedStepIndex ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'}`}>{i + 1}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-gray-900 capitalize">{s.channel}</span>
                      <span className="block text-xs text-gray-500 truncate">{s.delay_hours ? `after ${s.delay_hours}h · ` : 'immediately · '}{(s.content || '').replace(/<[^>]+>/g, '').slice(0, 40) || 'No content yet'}</span>
                    </span>
                  </button>
                ))}
                {steps.length === 0 && <p className="text-sm text-gray-400 py-2">No steps yet.</p>}
              </div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <label className="text-sm text-gray-700">Schedule (optional)
                <input type="datetime-local" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </label>
              <p className="text-xs text-gray-500 mt-1">Leave empty to launch on save.</p>
            </div>
          </aside>

          {/* Step editor — the main message composer */}
          <main className="bg-white rounded-lg border border-gray-200 p-6 min-h-[440px]">
            {steps.length === 0 || !steps[selectedStepIndex] ? (
              <div className="flex flex-col items-center justify-center text-center py-20">
                <p className="text-gray-500 mb-3">Add a step to start composing your message.</p>
                <button onClick={addStep} className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2"><Plus className="h-4 w-4" /> Add step</button>
              </div>
            ) : (() => {
              const i = selectedStepIndex;
              const s = steps[i];
              return (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-gray-900">Step {i + 1}</h2>
                    {steps.length > 1 && <button onClick={() => removeStep(i)} className="text-sm text-red-600 hover:text-red-800">Remove step</button>}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <label className="text-sm font-medium text-gray-700">Channel
                      <select value={s.channel} onChange={(e) => updateStep(i, { channel: e.target.value as ChannelType })} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg">
                        {(['email', 'sms', 'whatsapp', 'voice'] as ChannelType[]).map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </label>
                    <label className="text-sm font-medium text-gray-700">Send after (hours)
                      <input type="number" min={0} value={s.delay_hours} onChange={(e) => updateStep(i, { delay_hours: Number(e.target.value) || 0 })} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" />
                      <span className="block text-xs text-gray-500 mt-1">{i === 0 ? 'After launch' : 'After the previous step'}</span>
                    </label>
                  </div>
                  {s.channel === 'email' && (
                    <label className="block text-sm font-medium text-gray-700">Subject
                      <input value={s.subject || ''} onChange={(e) => updateStep(i, { subject: e.target.value })} placeholder="Email subject" className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" />
                    </label>
                  )}
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-gray-500">Insert:</span>
                    {['first_name', 'last_name', 'company', 'email'].map((tok) => (
                      <button key={tok} type="button" onClick={() => updateStep(i, { content: (s.content || '') + ` {{${tok}}}` })} className="px-2 py-0.5 text-xs rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50">{`{{${tok}}}`}</button>
                    ))}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                    {s.channel === 'email' ? (
                      <RichTextEditor key={i} content={s.content || ''} onChange={(html) => updateStep(i, { content: html })} placeholder="Compose your email…" minHeight="280px" />
                    ) : (
                      <>
                        <textarea value={s.content || ''} onChange={(e) => updateStep(i, { content: e.target.value })} rows={10} placeholder={`Write your ${s.channel} message…`} className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-y text-sm" />
                        {s.channel === 'sms' && <p className="text-xs text-gray-500 mt-1">{(s.content || '').length} chars · {Math.ceil(((s.content || '').length) / 160) || 1} segment(s)</p>}
                      </>
                    )}
                  </div>
                </div>
              );
            })()}
          </main>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// CAMPAIGN DETAIL MODAL
// ============================================================================

// Inbound replies to a campaign (2-way inbox), shown inside the campaign view.
// Staff can read each reply (with AI sentiment/intent + a suggested reply) and
// send a response that's actually delivered via SMS/email.
function CampaignRepliesSection({ campaignId, isOpen }: { campaignId: string; isOpen: boolean }) {
  const [replies, setReplies] = useState<any[] | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [sending, setSending] = useState<Record<string, boolean>>({});
  const [status, setStatus] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!isOpen || !campaignId) return;
    let cancel = false;
    setReplies(null);
    marketingApi.getCampaignResponses(campaignId).then((rows) => {
      if (!cancel) setReplies(rows || []);
    });
    return () => { cancel = true; };
  }, [isOpen, campaignId]);

  const send = async (r: any) => {
    const id = String(r.id);
    const message = (drafts[id] ?? r.ai_suggested_reply ?? '').trim();
    if (!message) { setStatus((s) => ({ ...s, [id]: 'Enter a reply first' })); return; }
    setSending((s) => ({ ...s, [id]: true }));
    setStatus((s) => ({ ...s, [id]: '' }));
    const res = await marketingApi.sendCampaignReply(campaignId, id, {
      message,
      send_via_channel: r.response_channel || 'sms',
    });
    setSending((s) => ({ ...s, [id]: false }));
    if (res.ok) {
      setStatus((s) => ({ ...s, [id]: 'Sent ✓' }));
      setReplies((prev) => (prev || []).map((x) => (String(x.id) === id ? { ...x, human_reply: message, is_processed: true } : x)));
    } else {
      setStatus((s) => ({ ...s, [id]: res.error || 'Send failed' }));
    }
  };

  const sentimentColor = (s?: string) =>
    s === 'positive' ? 'bg-green-100 text-green-700'
    : s === 'negative' ? 'bg-red-100 text-red-700'
    : s === 'urgent' ? 'bg-orange-100 text-orange-700'
    : 'bg-gray-100 text-gray-600';

  return (
    <div className="p-4 border border-gray-200 rounded-lg">
      <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
        <Users className="h-4 w-4" /> Replies {replies ? `(${replies.length})` : ''}
      </h4>
      {replies === null && <p className="text-sm text-gray-400">Loading replies…</p>}
      {replies && replies.length === 0 && (
        <p className="text-sm text-gray-400">No replies yet. Inbound responses to this campaign will appear here.</p>
      )}
      <div className="space-y-3 max-h-[340px] overflow-y-auto">
        {(replies || []).map((r: any) => {
          const id = String(r.id);
          return (
            <div key={id} className="p-3 bg-gray-50 rounded-lg border border-gray-100">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-sm font-medium text-gray-900">{r.member_name || 'Contact'}{r.member_phone ? ` · ${r.member_phone}` : ''}</span>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">{r.response_channel || 'sms'}</span>
                  {r.ai_sentiment && <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${sentimentColor(r.ai_sentiment)}`}>{r.ai_sentiment}</span>}
                </div>
              </div>
              <p className="text-sm text-gray-700 mb-2">{r.response_content || <span className="italic text-gray-400">(no content)</span>}</p>
              {r.human_reply ? (
                <div className="text-sm text-green-700 bg-green-50 border border-green-100 rounded px-2 py-1.5">Replied: {r.human_reply}</div>
              ) : (
                <div className="space-y-1.5">
                  <textarea
                    value={drafts[id] ?? r.ai_suggested_reply ?? ''}
                    onChange={(e) => setDrafts((d) => ({ ...d, [id]: e.target.value }))}
                    placeholder={`Reply via ${r.response_channel || 'sms'}…`}
                    rows={2}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-300"
                  />
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => send(r)}
                      disabled={!!sending[id]}
                      className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {sending[id] ? 'Sending…' : 'Send reply'}
                    </button>
                    {r.ai_suggested_reply && (drafts[id] ?? '') === '' && <span className="text-xs text-gray-400">AI suggestion pre-filled</span>}
                    {status[id] && <span className={`text-xs ${status[id].includes('✓') ? 'text-green-600' : 'text-red-600'}`}>{status[id]}</span>}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface CampaignDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaign: CampaignDetails | null;
  onEdit: () => void;
}

function CampaignDetailModal({ isOpen, onClose, campaign, onEdit }: CampaignDetailModalProps) {
  if (!campaign) return null;

  const typeInfo = getCampaignTypeInfo(campaign.type, campaign.channels);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>{typeInfo.icon}</span>
            {campaign.name}
          </DialogTitle>
          <DialogDescription>
            Campaign Details
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-4">
          {/* Status Badge */}
          <div className="flex items-center justify-between">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              campaign.status === 'completed' ? 'bg-green-100 text-green-800' :
              campaign.status === 'running' ? 'bg-blue-100 text-blue-800' :
              campaign.status === 'scheduled' ? 'bg-yellow-100 text-yellow-800' :
              campaign.status === 'paused' ? 'bg-orange-100 text-orange-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
            </span>
            <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${typeInfo.color}`}>
              {typeInfo.label}
            </span>
          </div>

          {/* Campaign Info Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Subject</p>
              <p className="font-medium text-gray-900">{campaign.subject || 'Not set'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Type</p>
              <p className="font-medium text-gray-900">{typeInfo.label}</p>
            </div>
            {campaign.scheduledDate && (
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Scheduled</p>
                <p className="font-medium text-gray-900">
                  {new Date(campaign.scheduledDate).toLocaleString()}
                </p>
              </div>
            )}
          </div>

          {/* Audience Info */}
          <div className="p-4 border border-gray-200 rounded-lg">
            <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <Users className="h-4 w-4" />
              Target Audience
            </h4>
            {campaign.audienceName ? (
              <div>
                <p className="font-medium text-gray-900">{campaign.audienceName}</p>
                <p className="text-sm text-gray-500">{campaign.audienceCount?.toLocaleString() || 0} contacts</p>
              </div>
            ) : campaign.organizationName ? (
              <div>
                <p className="font-medium text-gray-900">{campaign.organizationName}</p>
                <p className="text-sm text-gray-500">Organization-wide</p>
              </div>
            ) : (
              <p className="text-gray-400 italic">No audience selected</p>
            )}
          </div>

          {/* Performance Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-blue-600">{campaign.sentCount.toLocaleString()}</p>
              <p className="text-xs text-blue-600 uppercase tracking-wider">Sent</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-green-600">{campaign.openRate}%</p>
              <p className="text-xs text-green-600 uppercase tracking-wider">Open Rate</p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-purple-600">{campaign.clickRate}%</p>
              <p className="text-xs text-purple-600 uppercase tracking-wider">Click Rate</p>
            </div>
          </div>

          {/* Description */}
          {campaign.description && (
            <div className="p-4 border border-gray-200 rounded-lg">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Description</h4>
              <p className="text-gray-600">{campaign.description}</p>
            </div>
          )}

          {/* 2-way inbox replies to this campaign */}
          <CampaignRepliesSection campaignId={campaign.id} isOpen={isOpen} />
        </div>
        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
          <button
            type="button"
            onClick={() => {
              onClose();
              onEdit();
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Edit className="h-4 w-4" />
            Edit Campaign
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// EDIT CAMPAIGN MODAL
// ============================================================================

interface EditCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaign: CampaignDetails | null;
  onSubmit: (campaignId: string, updates: Partial<CampaignDetails>) => void;
  contactLists: ContactListFull[];
  loadingContactLists: boolean;
}

function EditCampaignModal({ isOpen, onClose, campaign, onSubmit, contactLists, loadingContactLists }: EditCampaignModalProps) {
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [type, setType] = useState<Campaign['type']>('email');
  const [selectedChannels, setSelectedChannels] = useState<ChannelType[]>([]);
  const [scheduledDate, setScheduledDate] = useState('');
  const [selectedListId, setSelectedListId] = useState<string>('');
  const [description, setDescription] = useState('');

  // Load campaign data when modal opens
  useEffect(() => {
    if (campaign && isOpen) {
      setName(campaign.name);
      setSubject(campaign.subject || '');
      setType(campaign.type);
      setSelectedChannels(campaign.channels || []);
      setScheduledDate(campaign.scheduledDate || '');
      setSelectedListId(campaign.audienceId || '');
      setDescription(campaign.description || '');
    }
  }, [campaign, isOpen]);

  const handleChannelToggle = (channel: ChannelType) => {
    setSelectedChannels(prev =>
      prev.includes(channel)
        ? prev.filter(c => c !== channel)
        : [...prev, channel]
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!campaign) return;

    const selectedList = contactLists.find(l => l.id === selectedListId);
    const updates: Partial<CampaignDetails> = {
      name,
      subject,
      type,
      scheduledDate: scheduledDate || undefined,
      description,
      audienceId: selectedListId || undefined,
      audienceName: selectedList?.name,
      audienceCount: selectedList?.member_count,
    };

    if (type === 'multi-channel' && selectedChannels.length > 0) {
      updates.channels = selectedChannels;
    }

    onSubmit(campaign.id, updates);
    onClose();
  };

  if (!campaign) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Campaign</DialogTitle>
          <DialogDescription>
            Update campaign settings and targeting.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Campaign Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subject / Message *
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Optional campaign description"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Campaign Type
            </label>
            <select
              value={type}
              onChange={(e) => {
                const newType = e.target.value as Campaign['type'];
                setType(newType);
                if (newType !== 'multi-channel') {
                  setSelectedChannels([]);
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="email">📧 Email Campaign</option>
              <option value="sms">📱 SMS Campaign</option>
              <option value="whatsapp">💬 WhatsApp Campaign</option>
              <option value="voice">📞 Voice AI Campaign</option>
              <option value="social">🌐 Social Media Campaign</option>
              <option value="multi-channel">🔀 Multi-Channel Campaign</option>
            </select>
          </div>

          {type === 'multi-channel' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Channels *
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {CHANNEL_OPTIONS.map((channel) => (
                  <label
                    key={channel.value}
                    className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                      selectedChannels.includes(channel.value)
                        ? 'bg-blue-50 border-blue-500 text-blue-700'
                        : 'bg-white border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedChannels.includes(channel.value)}
                      onChange={() => handleChannelToggle(channel.value)}
                      className="sr-only"
                    />
                    <span>{channel.icon}</span>
                    <span className="text-sm">{channel.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Audience Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Audience (Contact List)
            </label>
            {loadingContactLists ? (
              <div className="flex items-center gap-2 py-2 text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">Loading contact lists...</span>
              </div>
            ) : (
              <select
                value={selectedListId}
                onChange={(e) => setSelectedListId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">-- Select a contact list --</option>
                {contactLists.map((list) => (
                  <option key={list.id} value={list.id}>
                    {list.name} ({list.member_count} contacts) {list.list_type === 'dynamic' ? '[Dynamic]' : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Schedule (Optional)
            </label>
            <input
              type="datetime-local"
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Save Changes
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface CreateAutomationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    name: string;
    trigger_type: string;
    trigger_config?: Record<string, unknown>;
    action_type: string;
    action_config?: Record<string, unknown>;
  }) => void;
  contactLists: Array<{ id: string; name: string }>;
  campaigns: Array<{ id: string; name: string }>;
  /** When set, the modal edits this automation instead of creating one. */
  automation?: Automation | null;
}

// Reverse of TRIGGER_MAP: engine trigger_type + config → the human label, so the
// edit modal can pre-select the right option.
function labelForTrigger(triggerType?: string, config?: Record<string, unknown>): string {
  if (!triggerType) return '';
  const entries = Object.entries(TRIGGER_MAP);
  // Prefer an exact match on trigger_type + signal_type/min_score.
  const exact = entries.find(([, v]) => {
    if (v.trigger_type !== triggerType) return false;
    const c = v.trigger_config || {};
    if ('signal_type' in c) return c.signal_type === (config || {}).signal_type;
    if ('min_score' in c) return (config || {}).min_score !== undefined;
    return true;
  });
  if (exact) return exact[0];
  const byType = entries.find(([, v]) => v.trigger_type === triggerType);
  return byType ? byType[0] : '';
}

// Maps a human trigger label → engine trigger_type + trigger_config.
const TRIGGER_MAP: Record<string, { trigger_type: string; trigger_config?: Record<string, unknown> }> = {
  'New subscriber': { trigger_type: 'engagement', trigger_config: { event: 'new_subscriber' } },
  'Downloaded whitepaper': { trigger_type: 'engagement', trigger_config: { event: 'download' } },
  'Visited pricing page': { trigger_type: 'engagement', trigger_config: { event: 'pricing_visit' } },
  'Started trial': { trigger_type: 'engagement', trigger_config: { event: 'trial_start' } },
  'Inactive for 30 days': { trigger_type: 'engagement', trigger_config: { event: 'inactive_30d' } },
  'Made a purchase': { trigger_type: 'engagement', trigger_config: { event: 'purchase' } },
  'Abandoned cart': { trigger_type: 'engagement', trigger_config: { event: 'abandoned_cart' } },
  'Intent: pricing page': { trigger_type: 'intent_signal', trigger_config: { signal_type: 'pricing_page' } },
  'Intent: demo request': { trigger_type: 'intent_signal', trigger_config: { signal_type: 'demo_request' } },
  'Intent: content download': { trigger_type: 'intent_signal', trigger_config: { signal_type: 'content_download' } },
  'Intent: competitor research': { trigger_type: 'intent_signal', trigger_config: { signal_type: 'competitor_research' } },
  'Intent: job posting': { trigger_type: 'intent_signal', trigger_config: { signal_type: 'job_posting' } },
  'High intent score': { trigger_type: 'intent_signal', trigger_config: { min_score: 70 } },
  'HRIS contact synced': { trigger_type: 'hris_contact_synced' },
  'Visitor identified': { trigger_type: 'visitor_identified' },
  'Lead Builder match': { trigger_type: 'lead_builder_match' },
  'CRM record created': { trigger_type: 'crm_record_created' },
  'Contact added to a list': { trigger_type: 'contact_added_to_list' },
  'Custom trigger': { trigger_type: 'manual' },
};

function CreateAutomationModal({ isOpen, onClose, onSubmit, contactLists, campaigns, automation }: CreateAutomationModalProps) {
  const editing = !!automation;
  const [name, setName] = useState('');
  const [trigger, setTrigger] = useState('');
  const [actionType, setActionType] = useState<'add_to_list' | 'enroll_in_campaign'>('enroll_in_campaign');
  const [targetId, setTargetId] = useState('');

  // Prefill when opening in edit mode (or reset for create).
  useEffect(() => {
    if (!isOpen) return;
    if (automation) {
      setName(automation.name || '');
      setTrigger(labelForTrigger(automation.triggerType, automation.triggerConfig));
      setActionType((automation.actionType as 'add_to_list' | 'enroll_in_campaign') || 'enroll_in_campaign');
      const ac = (automation.actionConfig || {}) as Record<string, string>;
      setTargetId(ac.list_id || ac.campaign_id || '');
    } else {
      setName('');
      setTrigger('');
      setActionType('enroll_in_campaign');
      setTargetId('');
    }
  }, [isOpen, automation]);

  // Funnel-grouped triggers. Intent + Integration + List groups connect the
  // top-of-funnel sources (Intelligence / Integrations / Outreach) into nurture
  // automations — the "event triggers" spine (#2).
  const triggerGroups: Array<{ label: string; options: string[] }> = [
    {
      label: 'Engagement',
      options: ['New subscriber', 'Downloaded whitepaper', 'Visited pricing page', 'Started trial', 'Inactive for 30 days', 'Made a purchase', 'Abandoned cart'],
    },
    {
      label: 'Intent signals',
      options: ['Intent: pricing page', 'Intent: demo request', 'Intent: content download', 'Intent: competitor research', 'Intent: job posting', 'High intent score'],
    },
    {
      label: 'Integrations',
      options: ['HRIS contact synced', 'Visitor identified', 'Lead Builder match', 'CRM record created'],
    },
    {
      label: 'Audience',
      options: ['Contact added to a list', 'Custom trigger'],
    },
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const mapped = TRIGGER_MAP[trigger] || { trigger_type: 'manual' };
    const action_config = actionType === 'add_to_list' ? { list_id: targetId } : { campaign_id: targetId };
    onSubmit({
      name,
      trigger_type: mapped.trigger_type,
      trigger_config: mapped.trigger_config || {},
      action_type: actionType,
      action_config,
    });
    setName('');
    setTrigger('');
    setActionType('enroll_in_campaign');
    setTargetId('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Automation' : 'Create Automation Workflow'}</DialogTitle>
          <DialogDescription>
            Set up an automated sequence triggered by user actions.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Automation Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter automation name"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Trigger Event *
            </label>
            <select
              value={trigger}
              onChange={(e) => setTrigger(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select a trigger...</option>
              {triggerGroups.map((g) => (
                <optgroup key={g.label} label={g.label}>
                  {g.options.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Then *</label>
              <select
                value={actionType}
                onChange={(e) => { setActionType(e.target.value as 'add_to_list' | 'enroll_in_campaign'); setTargetId(''); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="enroll_in_campaign">Enroll in campaign</option>
                <option value="add_to_list">Add to list</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{actionType === 'add_to_list' ? 'List *' : 'Campaign *'}</label>
              <select
                value={targetId}
                onChange={(e) => setTargetId(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Select…</option>
                {(actionType === 'add_to_list' ? contactLists : campaigns).map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </div>
          </div>
          <p className="text-xs text-gray-500 -mt-2">When the trigger fires, the matched contact is {actionType === 'add_to_list' ? 'added to the list' : 'enrolled into the campaign drip'}.</p>
          <DialogFooter>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {editing ? 'Save changes' : 'Create Automation'}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface CreateListModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (list: Omit<ContactList, 'id' | 'count'> & { description?: string; filter_criteria?: Record<string, unknown>; selectedContactIds?: string[] }) => void;
  editList?: ContactListFull | null;
}

function CreateListModal({ isOpen, onClose, onSubmit, editList }: CreateListModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<ContactList['type']>('static');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Dynamic list filter criteria
  const [lifecycleStage, setLifecycleStage] = useState('');
  const [status, setStatus] = useState('');
  const [minScore, setMinScore] = useState('');
  const [maxScore, setMaxScore] = useState('');
  const [doNotEmail, setDoNotEmail] = useState<boolean | null>(null);

  // Preview state for dynamic lists
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState<{
    total_count: number;
    preview_contacts: Array<{
      id: string;
      email: string;
      first_name: string;
      last_name: string;
      company_name?: string;
      lifecycle_stage?: string;
      status?: string;
      score: number;
    }>;
  } | null>(null);

  // Static list contact selection
  const [availableContacts, setAvailableContacts] = useState<Array<{
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    company_name?: string;
    lifecycle_stage?: string;
    status?: string;
    score: number;
  }>>([]);
  const [selectedContactIds, setSelectedContactIds] = useState<Set<string>>(new Set());
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactSearch, setContactSearch] = useState('');

  // Load edit data when editList changes
  useEffect(() => {
    if (editList) {
      setName(editList.name);
      setDescription(editList.description || '');
      setType(editList.list_type as 'static' | 'dynamic');
      if (editList.filter_criteria) {
        setLifecycleStage(editList.filter_criteria.lifecycle_stage || '');
        setStatus(editList.filter_criteria.status || '');
        setMinScore(editList.filter_criteria.min_score?.toString() || '');
        setMaxScore(editList.filter_criteria.max_score?.toString() || '');
        setDoNotEmail(editList.filter_criteria.do_not_email ?? null);
      }
    }
  }, [editList]);

  // Load available contacts for static lists
  useEffect(() => {
    if (isOpen && type === 'static') {
      loadAvailableContacts();
    }
  }, [isOpen, type, contactSearch]);

  // Auto-preview for dynamic lists when criteria change
  useEffect(() => {
    if (isOpen && type === 'dynamic') {
      const hasAnyCriteria = lifecycleStage || status || minScore || maxScore || doNotEmail !== null;
      if (hasAnyCriteria) {
        const debounceTimer = setTimeout(() => {
          loadPreview();
        }, 500);
        return () => clearTimeout(debounceTimer);
      } else {
        setPreviewData(null);
      }
    }
  }, [isOpen, type, lifecycleStage, status, minScore, maxScore, doNotEmail]);

  const loadAvailableContacts = async () => {
    setContactsLoading(true);
    try {
      const result = await contactListsApi.getAvailableContacts({
        search: contactSearch || undefined,
        exclude_list_id: editList?.id,
        limit: 100,
      });
      setAvailableContacts(result.contacts);
    } catch (error) {
      console.error('Failed to load contacts:', error);
    } finally {
      setContactsLoading(false);
    }
  };

  const loadPreview = async () => {
    setPreviewLoading(true);
    try {
      const filterCriteria: {
        lifecycle_stage?: string;
        status?: string;
        min_score?: number;
        max_score?: number;
        do_not_email?: boolean;
      } = {};
      if (lifecycleStage) filterCriteria.lifecycle_stage = lifecycleStage;
      if (status) filterCriteria.status = status;
      if (minScore) filterCriteria.min_score = parseInt(minScore);
      if (maxScore) filterCriteria.max_score = parseInt(maxScore);
      if (doNotEmail !== null) filterCriteria.do_not_email = doNotEmail;

      const result = await contactListsApi.previewDynamicList(filterCriteria, 10);
      setPreviewData(result);
    } catch (error) {
      console.error('Failed to load preview:', error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setType('static');
    setLifecycleStage('');
    setStatus('');
    setMinScore('');
    setMaxScore('');
    setDoNotEmail(null);
    setPreviewData(null);
    setSelectedContactIds(new Set());
    setContactSearch('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Build filter criteria for dynamic lists
    const filter_criteria: Record<string, unknown> = {};
    if (type === 'dynamic') {
      if (lifecycleStage) filter_criteria.lifecycle_stage = lifecycleStage;
      if (status) filter_criteria.status = status;
      if (minScore) filter_criteria.min_score = parseInt(minScore);
      if (maxScore) filter_criteria.max_score = parseInt(maxScore);
      if (doNotEmail !== null) filter_criteria.do_not_email = doNotEmail;
    }

    try {
      await onSubmit({
        name,
        type,
        description: description || undefined,
        filter_criteria: Object.keys(filter_criteria).length > 0 ? filter_criteria : undefined,
        selectedContactIds: type === 'static' ? Array.from(selectedContactIds) : undefined,
      });
      resetForm();
      onClose();
    } catch {
      // Error handling is done in parent
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const toggleContactSelection = (contactId: string) => {
    setSelectedContactIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(contactId)) {
        newSet.delete(contactId);
      } else {
        newSet.add(contactId);
      }
      return newSet;
    });
  };

  const selectAllContacts = () => {
    setSelectedContactIds(new Set(availableContacts.map(c => c.id)));
  };

  const clearAllContacts = () => {
    setSelectedContactIds(new Set());
  };

  const isEditing = !!editList;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit List' : 'Create New List'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update your contact list settings and criteria.'
              : 'Create a list to segment your contacts for targeted campaigns.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              List Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter list name"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter a description for this list"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {!isEditing && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                List Type
              </label>
              <div className="space-y-2">
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="listType"
                    value="static"
                    checked={type === 'static'}
                    onChange={() => setType('static')}
                    className="text-blue-600"
                  />
                  <div>
                    <p className="font-medium text-gray-900">Static List</p>
                    <p className="text-sm text-gray-500">Manually add/remove contacts</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="listType"
                    value="dynamic"
                    checked={type === 'dynamic'}
                    onChange={() => setType('dynamic')}
                    className="text-blue-600"
                  />
                  <div>
                    <p className="font-medium text-gray-900">Dynamic Segment</p>
                    <p className="text-sm text-gray-500">Auto-updates based on criteria</p>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Static List Contact Selection */}
          {type === 'static' && (
            <div className="space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-gray-900">Select Contacts</h4>
                <span className="text-sm text-gray-500">
                  {selectedContactIds.size} selected
                </span>
              </div>

              {/* Search input */}
              <input
                type="text"
                value={contactSearch}
                onChange={(e) => setContactSearch(e.target.value)}
                placeholder="Search contacts by name or email..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />

              {/* Select/Clear All buttons */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={selectAllContacts}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  Select all
                </button>
                <span className="text-gray-300">|</span>
                <button
                  type="button"
                  onClick={clearAllContacts}
                  className="text-sm text-gray-600 hover:text-gray-700"
                >
                  Clear all
                </button>
              </div>

              {/* Contact list */}
              <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg bg-white">
                {contactsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                    <span className="ml-2 text-sm text-gray-500">Loading contacts...</span>
                  </div>
                ) : availableContacts.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm">No contacts found</p>
                    <p className="text-xs text-gray-400 mt-1">Create contacts in the Contacts tab first</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {availableContacts.map((contact) => (
                      <label
                        key={contact.id}
                        className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedContactIds.has(contact.id)}
                          onChange={() => toggleContactSelection(contact.id)}
                          className="h-4 w-4 text-blue-600 rounded"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {contact.first_name} {contact.last_name}
                          </p>
                          <p className="text-xs text-gray-500 truncate">{contact.email}</p>
                        </div>
                        {contact.company_name && (
                          <span className="text-xs text-gray-400 truncate max-w-[100px]">
                            {contact.company_name}
                          </span>
                        )}
                        <span className={`px-2 py-0.5 text-xs rounded ${
                          contact.lifecycle_stage === 'customer' ? 'bg-green-100 text-green-700' :
                          contact.lifecycle_stage === 'lead' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {contact.lifecycle_stage || 'subscriber'}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Dynamic List Criteria */}
          {type === 'dynamic' && (
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="font-medium text-gray-900">Filter Criteria</h4>
              <p className="text-sm text-gray-500">Define conditions to automatically include contacts in this segment.</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Lifecycle Stage
                  </label>
                  <select
                    value={lifecycleStage}
                    onChange={(e) => setLifecycleStage(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Any stage</option>
                    <option value="subscriber">Subscriber</option>
                    <option value="lead">Lead</option>
                    <option value="mql">Marketing Qualified Lead</option>
                    <option value="sql">Sales Qualified Lead</option>
                    <option value="opportunity">Opportunity</option>
                    <option value="customer">Customer</option>
                    <option value="evangelist">Evangelist</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Any status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="bounced">Bounced</option>
                    <option value="unsubscribed">Unsubscribed</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Minimum Score
                  </label>
                  <input
                    type="number"
                    value={minScore}
                    onChange={(e) => setMinScore(e.target.value)}
                    placeholder="e.g., 50"
                    min="0"
                    max="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Maximum Score
                  </label>
                  <input
                    type="number"
                    value={maxScore}
                    onChange={(e) => setMaxScore(e.target.value)}
                    placeholder="e.g., 100"
                    min="0"
                    max="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Preference
                </label>
                <select
                  value={doNotEmail === null ? '' : doNotEmail ? 'true' : 'false'}
                  onChange={(e) => {
                    if (e.target.value === '') setDoNotEmail(null);
                    else setDoNotEmail(e.target.value === 'true');
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Any preference</option>
                  <option value="false">Opted in to email</option>
                  <option value="true">Opted out of email</option>
                </select>
              </div>

              {/* Preview Section */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="text-sm font-medium text-gray-700">Preview Matching Contacts</h5>
                  {previewLoading && <Loader2 className="h-4 w-4 animate-spin text-blue-600" />}
                </div>

                {previewData ? (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Users className="h-5 w-5 text-blue-600" />
                      <span className="font-medium text-gray-900">{previewData.total_count}</span>
                      <span className="text-sm text-gray-500">contacts match these criteria</span>
                    </div>

                    {previewData.preview_contacts.length > 0 ? (
                      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
                        <div className="max-h-40 overflow-y-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 sticky top-0">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Name</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Email</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Stage</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Score</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {previewData.preview_contacts.map((contact) => (
                                <tr key={contact.id}>
                                  <td className="px-3 py-2 text-sm text-gray-900">
                                    {contact.first_name} {contact.last_name}
                                  </td>
                                  <td className="px-3 py-2 text-sm text-gray-500">{contact.email}</td>
                                  <td className="px-3 py-2">
                                    <span className={`px-2 py-0.5 text-xs rounded ${
                                      contact.lifecycle_stage === 'customer' ? 'bg-green-100 text-green-700' :
                                      contact.lifecycle_stage === 'lead' ? 'bg-blue-100 text-blue-700' :
                                      'bg-gray-100 text-gray-600'
                                    }`}>
                                      {contact.lifecycle_stage || 'subscriber'}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 text-sm text-gray-900">{contact.score}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {previewData.total_count > 10 && (
                          <div className="px-3 py-2 bg-gray-50 text-xs text-gray-500 text-center">
                            Showing 10 of {previewData.total_count} contacts
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No contacts match the current criteria</p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic">
                    Select filter criteria above to preview matching contacts
                  </p>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || (type === 'static' && selectedContactIds.size === 0 && !isEditing)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {isSubmitting ? (isEditing ? 'Saving...' : 'Creating...') : (isEditing ? 'Save Changes' : 'Create List')}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// TAB COMPONENTS
// ============================================================================

interface CampaignsTabProps {
  campaigns: CampaignDetails[];
  loading: boolean;
  onCreateCampaign: () => void;
  onToggleStatus: (campaign: CampaignDetails) => void;
  onRefresh: () => void;
  onView: (campaign: CampaignDetails) => void;
  onEdit: (campaign: CampaignDetails) => void;
}

function CampaignsTab({ campaigns, loading, onCreateCampaign, onToggleStatus, onRefresh, onView, onEdit }: CampaignsTabProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Loading campaigns...</span>
      </div>
    );
  }

  if (campaigns.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
        <Mail className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Campaigns Yet</h3>
        <p className="text-gray-500 mb-6">Create your first campaign to start reaching your audience.</p>
        <button
          onClick={onCreateCampaign}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Create Campaign
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Campaigns</h3>
        <div className="flex items-center gap-3">
          <button
            onClick={onRefresh}
            className="flex items-center gap-2 px-3 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          <button
            onClick={onCreateCampaign}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Create Campaign
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Channel</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Audience</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sent</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Open Rate</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Click Rate</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {campaigns.map((campaign) => {
              const typeInfo = getCampaignTypeInfo(campaign.type, campaign.channels);
              return (
              <tr key={campaign.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    {/* Triveny: Lucide channel icon; WeaveIn: legacy emoji (unchanged). */}
                    <CampaignChannelIcon type={(campaign.type as any) || 'email'} />
                    <div>
                      <div className="font-medium text-gray-900">{campaign.name}</div>
                      {campaign.description && (
                        <div className="text-sm text-gray-500 truncate max-w-xs">{campaign.description}</div>
                      )}
                      {campaign.scheduledDate && (
                        <div className="text-sm text-gray-500 flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Scheduled: {campaign.scheduledDate}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div>
                    {config.isTriveny ? (
                      // Triveny: mono UPPERCASE pill with Lucide icon, neutral or accent-soft
                      <CampaignChannelChip type={(campaign.type as any) || 'email'} />
                    ) : (
                      // WeaveIn: legacy emoji + rainbow color pill (unchanged)
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${typeInfo.color}`}>
                        {campaign.type === 'multi-channel' && campaign.channels && (
                          <span className="text-xs">{campaign.channels.map(c => CHANNEL_OPTIONS.find(o => o.value === c)?.icon).join('')}</span>
                        )}
                        {typeInfo.label}
                      </span>
                    )}
                    {campaign.templateName && (
                      <div className="text-xs text-gray-500 mt-1">Template: {campaign.templateName}</div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div>
                    {campaign.audienceName ? (
                      <>
                        <div className="text-sm font-medium text-gray-900">{campaign.audienceName}</div>
                        {campaign.audienceCount !== undefined && (
                          <div className="text-xs text-gray-500 flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {campaign.audienceCount.toLocaleString()} contacts
                          </div>
                        )}
                      </>
                    ) : campaign.organizationName ? (
                      <>
                        <div className="text-sm font-medium text-gray-900">{campaign.organizationName}</div>
                        <div className="text-xs text-gray-500">Organization</div>
                      </>
                    ) : (
                      <span className="text-sm text-gray-400">No audience set</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    campaign.status === 'completed' ? 'bg-green-100 text-green-800' :
                    campaign.status === 'running' ? 'bg-blue-100 text-blue-800' :
                    campaign.status === 'scheduled' ? 'bg-yellow-100 text-yellow-800' :
                    campaign.status === 'paused' ? 'bg-orange-100 text-orange-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {campaign.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">{campaign.sentCount.toLocaleString()}</td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {campaign.openRate > 0 ? (
                    config.isTriveny ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span className="tv-bar2"><i style={{ width: `${Math.min(100, campaign.openRate)}%` }} /></span>
                        <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 11, color: 'var(--v2-ink-700)' }}>
                          {campaign.openRate}%
                        </span>
                      </span>
                    ) : (
                      `${campaign.openRate}%`
                    )
                  ) : '-'}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {campaign.clickRate > 0 ? (
                    config.isTriveny ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span className="tv-bar2"><i style={{ width: `${Math.min(100, campaign.clickRate)}%` }} /></span>
                        <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 11, color: 'var(--v2-ink-700)' }}>
                          {campaign.clickRate}%
                        </span>
                      </span>
                    ) : (
                      `${campaign.clickRate}%`
                    )
                  ) : '-'}
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      className="p-1 hover:bg-gray-100 rounded"
                      title="View"
                      onClick={() => onView(campaign)}
                    >
                      <Eye className="h-4 w-4 text-gray-500" />
                    </button>
                    <button
                      className="p-1 hover:bg-gray-100 rounded"
                      title="Edit"
                      onClick={() => onEdit(campaign)}
                    >
                      <Edit className="h-4 w-4 text-gray-500" />
                    </button>
                    {campaign.status === 'draft' && (
                      <button
                        className="p-1 hover:bg-gray-100 rounded"
                        title="Send"
                        onClick={() => onToggleStatus(campaign)}
                      >
                        <Send className="h-4 w-4 text-blue-500" />
                      </button>
                    )}
                    {campaign.status === 'running' && (
                      <button
                        className="p-1 hover:bg-gray-100 rounded"
                        title="Pause"
                        onClick={() => onToggleStatus(campaign)}
                      >
                        <Pause className="h-4 w-4 text-orange-500" />
                      </button>
                    )}
                    {campaign.status === 'paused' && (
                      <button
                        className="p-1 hover:bg-gray-100 rounded"
                        title="Resume"
                        onClick={() => onToggleStatus(campaign)}
                      >
                        <Play className="h-4 w-4 text-green-500" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface AutomationTabProps {
  automations: Automation[];
  onCreateAutomation: () => void;
  onToggleStatus: (automation: Automation) => void;
  onEdit: (automation: Automation) => void;
}

function AutomationTab({ automations, onCreateAutomation, onToggleStatus, onEdit }: AutomationTabProps) {
  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Automation Workflows</h3>
        <button
          onClick={onCreateAutomation}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Create Automation
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {automations.map((automation) => (
          <div key={automation.id} className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${automation.status === 'active' ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <Zap className={`h-5 w-5 ${automation.status === 'active' ? 'text-green-600' : 'text-gray-500'}`} />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">{automation.name}</h4>
                  <p className="text-sm text-gray-500">{automation.trigger}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {automation.status === 'active' ? (
                  <button
                    className="p-1 hover:bg-gray-100 rounded"
                    title="Pause"
                    onClick={() => onToggleStatus(automation)}
                  >
                    <Pause className="h-4 w-4 text-gray-500" />
                  </button>
                ) : (
                  <button
                    className="p-1 hover:bg-gray-100 rounded"
                    title="Activate"
                    onClick={() => onToggleStatus(automation)}
                  >
                    <Play className="h-4 w-4 text-green-500" />
                  </button>
                )}
                <button className="p-1 hover:bg-gray-100 rounded" title="Edit" onClick={() => onEdit(automation)}>
                  <Edit className="h-4 w-4 text-gray-500" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div>
                <p className="text-2xl font-bold text-gray-900">{automation.enrolled}</p>
                <p className="text-sm text-gray-500">Enrolled</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{automation.completed}</p>
                <p className="text-sm text-gray-500">Completed</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{automation.steps}</p>
                <p className="text-sm text-gray-500">Steps</p>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                automation.status === 'active' ? 'bg-green-100 text-green-800' :
                automation.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {automation.status}
              </span>
              <span className="text-sm text-gray-500">
                {automation.enrolled > 0 ? Math.round((automation.completed / automation.enrolled) * 100) : 0}% completion rate
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

interface ListsTabProps {
  lists: ContactList[];
  onCreateList: () => void;
  onEditList: (list: ContactList) => void;
  onDeleteList: (listId: string) => void;
  onRefreshList?: (listId: string) => void;
}

function ListsTab({ lists, onCreateList, onEditList, onDeleteList, onRefreshList }: ListsTabProps) {
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [refreshingId, setRefreshingId] = useState<string | null>(null);

  const handleDelete = async (listId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this list? This action cannot be undone.')) {
      setDeletingId(listId);
      try {
        await onDeleteList(listId);
      } finally {
        setDeletingId(null);
      }
    }
  };

  const handleRefresh = async (listId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRefreshList) {
      setRefreshingId(listId);
      try {
        await onRefreshList(listId);
      } finally {
        setRefreshingId(null);
      }
    }
  };

  if (lists.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
        <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Lists Yet</h3>
        <p className="text-gray-500 mb-6">Create a list to segment your contacts for targeted campaigns.</p>
        <button
          onClick={onCreateList}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Create List
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Lists & Segments</h3>
        <button
          onClick={onCreateList}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Create List
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {lists.map((list) => (
          <div
            key={list.id}
            className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow group"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-gray-900 truncate">{list.name}</h4>
              </div>
              <div className="flex items-center gap-1 ml-2">
                <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                  list.type === 'dynamic' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                }`}>
                  {list.type}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-4">
              <Users className="h-5 w-5 text-gray-400" />
              <span className="text-2xl font-bold text-gray-900">{list.count.toLocaleString()}</span>
              <span className="text-sm text-gray-500">contacts</span>
            </div>

            {/* Action buttons - visible on hover */}
            <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100">
              {list.type === 'dynamic' && onRefreshList && (
                <button
                  onClick={(e) => handleRefresh(list.id, e)}
                  disabled={refreshingId === list.id}
                  className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                  title="Refresh list members"
                >
                  {refreshingId === list.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEditList(list);
                }}
                className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Edit list"
              >
                <Edit className="h-4 w-4" />
              </button>
              <button
                onClick={(e) => handleDelete(list.id, e)}
                disabled={deletingId === list.id}
                className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                title="Delete list"
              >
                {deletingId === list.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AnalyticsTab() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totals, setTotals] = useState<{ campaigns: number; sent: number; failed: number; pending: number; replies: number; opened: number; clicked: number } | null>(null);
  const [rows, setRows] = useState<Array<{ id: string; name: string; status: string; channels: string[]; sent: number; failed: number; pending: number; replies: number; opened: number; clicked: number; reply_rate: number }>>([]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res: any = await marketingApi.getCampaignsSummary();
        const data = res?.data ?? res ?? {};
        if (cancel) return;
        setTotals(data.totals || { campaigns: 0, sent: 0, failed: 0, pending: 0, replies: 0, opened: 0, clicked: 0 });
        setRows(Array.isArray(data.campaigns) ? data.campaigns : []);
      } catch (err) {
        if (!cancel) setError('Failed to load campaign performance');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const statusPill = (s: string) => {
    const cls = s === 'active' ? 'bg-green-100 text-green-700'
      : s === 'completed' ? 'bg-blue-100 text-blue-700'
      : s === 'paused' ? 'bg-orange-100 text-orange-700'
      : 'bg-gray-100 text-gray-600';
    return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium capitalize ${cls}`}>{s}</span>;
  };

  const card = (label: string, value: number | string, sub?: string, tone?: string) => (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <p className="text-sm text-gray-600">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${tone || 'text-gray-900'}`}>{typeof value === 'number' ? value.toLocaleString() : value}</p>
      {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
    </div>
  );

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Campaign Performance</h3>
          <p className="text-sm text-gray-500">Across all channels — sends, replies, and engagement per campaign.</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading performance…</span>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            {card('Campaigns', totals?.campaigns ?? 0)}
            {card('Messages sent', totals?.sent ?? 0)}
            {card('Replies', totals?.replies ?? 0, undefined, 'text-violet-600')}
            {card('Opened', totals?.opened ?? 0)}
            {card('Clicked', totals?.clicked ?? 0)}
            {card('Pending', totals?.pending ?? 0, (totals?.failed ?? 0) > 0 ? `${totals?.failed} failed` : undefined)}
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100">
              <h4 className="font-medium text-gray-900">Per-campaign breakdown</h4>
            </div>
            {rows.length === 0 ? (
              <p className="text-sm text-gray-400 px-5 py-8 text-center">No campaign activity yet. Launch a campaign to see performance here.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-100">
                      <th className="px-5 py-2 font-medium">Campaign</th>
                      <th className="px-3 py-2 font-medium">Status</th>
                      <th className="px-3 py-2 font-medium">Channels</th>
                      <th className="px-3 py-2 font-medium text-right">Sent</th>
                      <th className="px-3 py-2 font-medium text-right">Opened</th>
                      <th className="px-3 py-2 font-medium text-right">Clicked</th>
                      <th className="px-3 py-2 font-medium text-right">Replies</th>
                      <th className="px-3 py-2 font-medium text-right">Reply %</th>
                      <th className="px-3 py-2 font-medium text-right">Pending</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="px-5 py-2.5 font-medium text-gray-900">{r.name}</td>
                        <td className="px-3 py-2.5">{statusPill(r.status)}</td>
                        <td className="px-3 py-2.5">
                          <div className="flex flex-wrap gap-1">
                            {(r.channels || []).length ? r.channels.map((c) => (
                              <span key={c} className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[11px] capitalize">{c}</span>
                            )) : <span className="text-gray-300">—</span>}
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{r.sent.toLocaleString()}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{r.opened.toLocaleString()}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{r.clicked.toLocaleString()}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{r.replies.toLocaleString()}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{r.reply_rate ? `${r.reply_rate}%` : '—'}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{r.pending ? r.pending.toLocaleString() : (r.failed ? <span className="text-red-600">{r.failed} failed</span> : '—')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export interface MarketingAutomationPageProps {
  /** Pre-select an internal tab on mount. Lets callers route directly into
      e.g. the Analytics sub-tab instead of the default Campaigns view. */
  initialTab?: MarketingTab;
}

export function MarketingAutomationPage({ initialTab = 'campaigns' }: MarketingAutomationPageProps = {}) {
  const { t } = useAppTranslation();
  const tabbed = useTabbedChrome();
  const [activeTab, setActiveTab] = useState<MarketingTab>(initialTab);

  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // State for data - start empty, API will populate
  const [campaigns, setCampaigns] = useState<CampaignDetails[]>([]);
  const [automations, setAutomations] = useState<Automation[]>([]);
  const [lists, setLists] = useState<ContactList[]>([]);

  // Modal states
  const [createCampaignOpen, setCreateCampaignOpen] = useState(false);
  const [createAutomationOpen, setCreateAutomationOpen] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState<Automation | null>(null);
  const [createListOpen, setCreateListOpen] = useState(false);
  const [editingList, setEditingList] = useState<ContactListFull | null>(null);

  // Campaign detail/edit modal states
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignDetails | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  // Full-page campaign editor state
  const [showCampaignEditor, setShowCampaignEditor] = useState(false);
  const [campaignEditorMode, setCampaignEditorMode] = useState<'create' | 'edit'>('create');
  const [editingCampaign, setEditingCampaign] = useState<CampaignDetails | null>(null);

  // Contact lists state for audience selection
  const [contactLists, setContactLists] = useState<ContactListFull[]>([]);
  const [loadingContactLists, setLoadingContactLists] = useState(false);

  // Fetch campaigns from API
  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await marketingApi.getCampaigns();
      if (response.success && response.data) {
        // Handle both { campaigns: [...] } and direct array response formats
        const campaignsArray = Array.isArray(response.data)
          ? response.data
          : (response.data as { campaigns?: ApiCampaign[] }).campaigns || [];
        // Map API response to CampaignDetails type
        const mappedCampaigns: CampaignDetails[] = campaignsArray.map((c: ApiCampaign) => ({
          id: c.id,
          name: c.name,
          type: (c.type || 'email') as Campaign['type'],
          channels: c.channels,
          status: c.status as Campaign['status'],
          sentCount: c.sentCount || 0,
          openRate: c.openRate || 0,
          clickRate: c.clickRate || 0,
          scheduledDate: c.scheduledAt,
          subject: c.subject,
          // Extended details from API
          description: c.content,
          audienceId: undefined, // To be fetched if available
          audienceName: undefined,
          audienceCount: undefined,
          templateId: undefined,
          templateName: undefined,
          organizationId: undefined,
          organizationName: undefined,
        }));
        setCampaigns(mappedCampaigns);
      } else {
        setError('Failed to load campaigns from server');
        setCampaigns([]);
      }
    } catch (err) {
      console.error('Failed to fetch campaigns:', err);
      setError('Unable to connect to server. Please try again.');
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch data on mount
  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  // Fetch contact lists for audience selection and lists tab
  const fetchContactLists = useCallback(async () => {
    setLoadingContactLists(true);
    try {
      const fetchedLists = await contactListsApi.listContactLists();
      setContactLists(fetchedLists);
      // Also update the local lists state for UI display
      setLists(fetchedLists.map(list => ({
        id: list.id,
        name: list.name,
        count: list.member_count,
        type: list.list_type,
      })));
    } catch (err) {
      console.error('Failed to fetch contact lists:', err);
      // Silently fail - contact lists are optional
    } finally {
      setLoadingContactLists(false);
    }
  }, []);

  // Fetch contact lists on mount
  useEffect(() => {
    fetchContactLists();
  }, [fetchContactLists]);

  const tabs: { id: MarketingTab; label: string; icon: React.ReactNode }[] = [
    { id: 'campaigns', label: t('platform.marketing.tabs.campaigns', 'Campaigns'), icon: <Mail className="h-4 w-4" /> },
    { id: 'automation', label: t('platform.marketing.tabs.automation', 'Automation'), icon: <Zap className="h-4 w-4" /> },
    { id: 'lists', label: t('platform.marketing.tabs.lists', 'Lists & Segments'), icon: <Target className="h-4 w-4" /> },
    { id: 'analytics', label: t('platform.marketing.tabs.analytics', 'Analytics'), icon: <BarChart3 className="h-4 w-4" /> },
    { id: 'integrations', label: t('platform.marketing.tabs.integrations', 'Integrations'), icon: <Plug className="h-4 w-4" /> },
  ];

  // Handlers
  const handleCreateCampaign = async (campaign: Omit<Campaign, 'id' | 'sentCount' | 'openRate' | 'clickRate'>) => {
    try {
      const response = await marketingApi.createCampaign({
        name: campaign.name,
        type: campaign.type === 'multi-channel' ? 'email' : campaign.type,
        subject: campaign.subject,
      });

      if (response.success && response.data) {
        // Refresh campaigns list
        fetchCampaigns();
      } else {
        setError('Failed to create campaign. Please try again.');
      }
    } catch (err) {
      console.error('Failed to create campaign:', err);
      setError('Failed to create campaign. Please try again.');
    }
  };

  const handleToggleCampaignStatus = async (campaign: CampaignDetails) => {
    try {
      let response;
      if (campaign.status === 'draft' || campaign.status === 'paused') {
        response = await marketingApi.sendCampaign(campaign.id);
      } else if (campaign.status === 'running') {
        response = await marketingApi.pauseCampaign(campaign.id);
      }

      if (response?.success) {
        fetchCampaigns();
      } else {
        setError('Failed to update campaign status.');
      }
    } catch (err) {
      console.error('Failed to toggle campaign status:', err);
      setError('Failed to update campaign status. Please try again.');
    }
  };

  // Automations now PERSIST via the live engine (/marketing/automations).
  const mapApiAutomation = (a: any): Automation => ({
    id: String(a.id),
    name: a.name,
    trigger: a.trigger_type,
    status: (a.status as Automation['status']) || 'active',
    enrolled: a.enrolled_count || 0,
    completed: 0,
    steps: 0,
    triggerType: a.trigger_type,
    triggerConfig: a.trigger_config || {},
    actionType: a.action_type,
    actionConfig: a.action_config || {},
  });
  const loadAutomations = useCallback(async () => {
    try {
      const res: any = await marketingApi.getAutomations();
      const rows = res?.data ?? res ?? [];
      if (Array.isArray(rows)) setAutomations(rows.map(mapApiAutomation));
    } catch {
      /* keep whatever's there */
    }
  }, []);
  useEffect(() => { loadAutomations(); }, [loadAutomations]);

  const handleCreateAutomation = async (data: {
    name: string;
    trigger_type: string;
    trigger_config?: Record<string, unknown>;
    action_type: string;
    action_config?: Record<string, unknown>;
  }) => {
    try {
      const res: any = await marketingApi.createAutomation({ ...data, status: 'active' });
      const created = res?.data ?? res;
      if (created?.id) setAutomations((prev) => [mapApiAutomation(created), ...prev]);
      else await loadAutomations();
    } catch (err) {
      console.error('Failed to create automation:', err);
      setError('Failed to create automation. Please try again.');
    }
  };

  const handleUpdateAutomation = async (
    id: string,
    data: { name: string; trigger_type: string; trigger_config?: Record<string, unknown>; action_type: string; action_config?: Record<string, unknown> },
  ) => {
    try {
      const res: any = await marketingApi.updateAutomation(id, data);
      const updated = res?.data ?? res;
      if (updated?.id) setAutomations((prev) => prev.map((a) => (a.id === id ? mapApiAutomation(updated) : a)));
      else await loadAutomations();
    } catch (err) {
      console.error('Failed to update automation:', err);
      setError('Failed to update automation. Please try again.');
    }
  };

  const handleToggleAutomationStatus = async (automation: Automation) => {
    const next = automation.status === 'active' ? 'paused' : 'active';
    setAutomations((prev) => prev.map((a) => (a.id === automation.id ? { ...a, status: next } : a)));
    try {
      await marketingApi.updateAutomation(automation.id, { status: next });
    } catch {
      loadAutomations();
    }
  };

  const handleCreateList = async (list: Omit<ContactList, 'id' | 'count'> & { description?: string; filter_criteria?: Record<string, unknown>; selectedContactIds?: string[] }) => {
    try {
      if (editingList) {
        // Update existing list
        await contactListsApi.updateContactList(editingList.id, {
          name: list.name,
          description: list.description,
          filter_criteria: list.filter_criteria,
        });
        setEditingList(null);
      } else {
        // Create new list
        const createdList = await contactListsApi.createContactList({
          name: list.name,
          list_type: list.type,
          description: list.description,
          filter_criteria: list.filter_criteria,
        });

        // If static list with selected contacts, add them
        if (list.type === 'static' && list.selectedContactIds && list.selectedContactIds.length > 0) {
          await contactListsApi.addMembers(createdList.id, list.selectedContactIds);
        }
      }
      // Refresh the lists from the server
      fetchContactLists();
    } catch (err) {
      console.error('Failed to create/update contact list:', err);
      setError('Failed to save list. Please try again.');
    }
  };

  const handleEditList = async (list: ContactList) => {
    try {
      // Fetch the full list details for editing
      const fullList = await contactListsApi.getContactList(list.id);
      setEditingList(fullList);
      setCreateListOpen(true);
    } catch (err) {
      console.error('Failed to load list for editing:', err);
      setError('Failed to load list details. Please try again.');
    }
  };

  const handleDeleteList = async (listId: string) => {
    try {
      await contactListsApi.deleteContactList(listId);
      // Refresh the lists from the server
      fetchContactLists();
    } catch (err) {
      console.error('Failed to delete contact list:', err);
      setError('Failed to delete list. Please try again.');
    }
  };

  const handleRefreshList = async (listId: string) => {
    try {
      await contactListsApi.refreshDynamicList(listId);
      // Refresh the lists from the server to get updated counts
      fetchContactLists();
    } catch (err) {
      console.error('Failed to refresh contact list:', err);
      setError('Failed to refresh list. Please try again.');
    }
  };

  // View campaign details
  const handleViewCampaign = (campaign: CampaignDetails) => {
    setSelectedCampaign(campaign);
    setDetailModalOpen(true);
  };

  // Open full-page campaign editor for creating new campaign
  const handleOpenCampaignEditor = () => {
    setCampaignEditorMode('create');
    setEditingCampaign(null);
    setShowCampaignEditor(true);
  };

  // Edit campaign - now opens full-page editor
  const handleEditCampaign = (campaign: CampaignDetails) => {
    setCampaignEditorMode('edit');
    setEditingCampaign(campaign);
    setShowCampaignEditor(true);
    // Close detail modal if open
    setDetailModalOpen(false);
  };

  // Close campaign editor
  const handleCloseCampaignEditor = () => {
    setShowCampaignEditor(false);
    setEditingCampaign(null);
  };

  // Save campaign from editor (handles both create and update)
  const handleSaveCampaignFromEditor = async (
    campaignData: Partial<CampaignDetails> & { channelContent?: Record<string, unknown>; steps?: any[] },
  ) => {
    // Backend (marketing_campaigns_router) fields: name, description,
    // audience_list_id. campaign_type is an enum that doesn't cover
    // whatsapp/voice/social, so we omit it (channels live on the steps).
    const audience_list_id = campaignData.audienceId || undefined;
    const steps = (campaignData.steps || []).map((s: any) => ({
      channel: s.channel,
      subject: s.subject || undefined,
      content: s.content || '',
      delay_hours: Number(s.delay_hours) || 0,
    }));
    let campaignId = campaignData.id;

    if (campaignEditorMode === 'create') {
      const response = await marketingApi.createCampaign({
        name: campaignData.name || '',
        description: campaignData.description,
        audience_list_id,
      } as any);
      if (!response.success || !response.data) {
        throw new Error('Failed to create campaign');
      }
      campaignId = (response.data as any).id;
    } else if (campaignData.id) {
      const response = await marketingApi.updateCampaign(campaignData.id, {
        name: campaignData.name,
        description: campaignData.description,
        audience_list_id,
      } as any);
      if (!response.success) {
        throw new Error('Failed to update campaign');
      }
      campaignId = campaignData.id;
    }

    // Persist the drip sequence steps.
    if (campaignId && steps.length) {
      await marketingApi.saveCampaignSteps(campaignId, steps);
    }

    // Refresh campaigns list
    fetchCampaigns();
  };

  // Update campaign via API (legacy - for edit modal)
  const handleUpdateCampaign = async (campaignId: string, updates: Partial<CampaignDetails>) => {
    try {
      const response = await marketingApi.updateCampaign(campaignId, {
        name: updates.name,
        subject: updates.subject,
        type: updates.type === 'multi-channel' ? 'email' : updates.type,
        content: updates.description,
      });

      if (response.success) {
        // Optimistically update local state
        setCampaigns(campaigns.map(c =>
          c.id === campaignId ? { ...c, ...updates } : c
        ));
        fetchCampaigns(); // Refresh from server
      } else {
        setError('Failed to update campaign. Please try again.');
      }
    } catch (err) {
      console.error('Failed to update campaign:', err);
      setError('Failed to update campaign. Please try again.');
    }
  };

  // If campaign editor is open, render it instead of the main page
  if (showCampaignEditor) {
    return (
      <CampaignEditor
        campaign={editingCampaign}
        contactLists={contactLists}
        loadingContactLists={loadingContactLists}
        onSave={handleSaveCampaignFromEditor}
        onBack={handleCloseCampaignEditor}
        mode={campaignEditorMode}
      />
    );
  }

  return (
    // Triveny.ai: .triveny-platform wrapper activates the design-system overlay
    // (src/styles/triveny-platform.css). WeaveIn users see the unmodified
    // Tailwind treatment.
    <div className={`p-6 max-w-7xl mx-auto${config.isTriveny ? ' triveny-platform' : ''}`}>
      {config.isTriveny && (
        <AgentAssistantPanel
          agentKey="sales.voice_concierge"
          title="Campaign assistant"
          greeting="I can list campaigns, read performance, and draft outreach sequences. What are we working on?"
          examples={['How are my campaigns performing?', 'Draft a 3-touch sequence for warm leads', 'List my active campaigns']}
        />
      )}
      {/* Header — hidden for Triveny; the TvPillarBar already shows "MARKET · Campaigns". */}
      {!config.isTriveny && (
        <div className="mb-8">
          {!tabbed && <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2"><Mail className="h-6 w-6 text-blue-600" />{t('platform.marketing.title', 'Marketing Automation')}</h1>}
          <p className="text-gray-600 mt-1">{t('platform.marketing.description', 'Manage campaigns, automations, and track performance')}</p>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Mail className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('platform.marketing.stats.activeCampaigns', 'Active Campaigns')}</p>
              <p className="text-xl font-bold text-gray-900">
                {campaigns.filter(c => c.status === 'running').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Zap className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('platform.marketing.stats.activeAutomations', 'Active Automations')}</p>
              <p className="text-xl font-bold text-gray-900">
                {automations.filter(a => a.status === 'active').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Users className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('platform.marketing.stats.contactLists', 'Contact Lists')}</p>
              <p className="text-xl font-bold text-gray-900">{lists.length.toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Clock className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('platform.marketing.stats.scheduled', 'Scheduled')}</p>
              <p className="text-xl font-bold text-gray-900">
                {campaigns.filter(c => c.status === 'scheduled').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-4 sm:space-x-8 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <p className="text-red-700">{error}</p>
          <button
            onClick={() => setError(null)}
            className="ml-auto p-1 hover:bg-red-100 rounded"
          >
            <X className="h-4 w-4 text-red-600" />
          </button>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'campaigns' && (
        <CampaignsTab
          campaigns={campaigns}
          loading={loading}
          onCreateCampaign={handleOpenCampaignEditor}
          onToggleStatus={handleToggleCampaignStatus}
          onRefresh={fetchCampaigns}
          onView={handleViewCampaign}
          onEdit={handleEditCampaign}
        />
      )}
      {activeTab === 'automation' && (
        <AutomationTab
          automations={automations}
          onCreateAutomation={() => { setEditingAutomation(null); setCreateAutomationOpen(true); }}
          onToggleStatus={handleToggleAutomationStatus}
          onEdit={(a) => { setEditingAutomation(a); setCreateAutomationOpen(true); }}
        />
      )}
      {activeTab === 'lists' && (
        <ListsTab
          lists={lists}
          onCreateList={() => {
            setEditingList(null);
            setCreateListOpen(true);
          }}
          onEditList={handleEditList}
          onDeleteList={handleDeleteList}
          onRefreshList={handleRefreshList}
        />
      )}
      {activeTab === 'analytics' && <AnalyticsTab />}
      {activeTab === 'integrations' && <MarketplaceIntegrationsPage />}

      {/* Modals */}
      <CreateCampaignModal
        isOpen={createCampaignOpen}
        onClose={() => setCreateCampaignOpen(false)}
        onSubmit={handleCreateCampaign}
        contactLists={contactLists}
        loadingContactLists={loadingContactLists}
      />
      <CreateAutomationModal
        isOpen={createAutomationOpen}
        onClose={() => { setCreateAutomationOpen(false); setEditingAutomation(null); }}
        automation={editingAutomation}
        onSubmit={(data) => { if (editingAutomation) handleUpdateAutomation(editingAutomation.id, data); else handleCreateAutomation(data); }}
        contactLists={contactLists.map((l: any) => ({ id: l.id, name: l.name }))}
        campaigns={campaigns.map((c: any) => ({ id: c.id, name: c.name }))}
      />
      <CreateListModal
        isOpen={createListOpen}
        onClose={() => {
          setCreateListOpen(false);
          setEditingList(null);
        }}
        onSubmit={handleCreateList}
        editList={editingList}
      />

      {/* Campaign Detail Modal */}
      <CampaignDetailModal
        isOpen={detailModalOpen}
        onClose={() => {
          setDetailModalOpen(false);
          setSelectedCampaign(null);
        }}
        campaign={selectedCampaign}
        onEdit={() => {
          if (selectedCampaign) {
            handleEditCampaign(selectedCampaign);
          }
        }}
      />

      {/* Edit Campaign Modal */}
      <EditCampaignModal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedCampaign(null);
        }}
        campaign={selectedCampaign}
        onSubmit={handleUpdateCampaign}
        contactLists={contactLists}
        loadingContactLists={loadingContactLists}
      />
    </div>
  );
}

export default MarketingAutomationPage;
